/**
 * ResultEntryQueryPanel — the structured query/filter region for Result Entry
 * Express, ported from the legacy BTLIMS Result Entry query panel.
 *
 * Layout (2026-08-27): a fixed 7-column × 2-row grid —
 *   col1 QC Batch ID / Job ID · col2 Sample Matrix / Test · col3 Project ID /
 *   Project Name · col4 Client / Site Name · col5 Date Analyzed F/T ·
 *   col6 Date Received F/T · col7 Date Collected F/T (SOON). Each date column
 *   keeps a SINGLE label on its From cell; the To cell sits directly below.
 * Lookup filters are CHECKED COMBOS (multi-select) with a header select-all;
 * values are sent '|'-joined and matched server-side (IN via STRING_SPLIT).
 */
import { useMemo, useState } from 'react';
import { LookupCombo } from '../../components/LookupCombo';
import './resultEntryQueryPanel.css';

/** A QC Batch option — value + the two display-only columns (batch Test + the
 *  analytical Method) shown in the 3-column picker: QC Batch ID · Test · Method
 *  (user request 2026-09-09). `emp` (creating Employee) is retained on the type
 *  for compatibility but is no longer displayed. */
export interface ReeBatchOption { id: string; test: string; method: string; emp?: string }

/** The query-panel filter values. Empty array/'' = "All" (no filter). */
export interface ReeFilters {
  batchId:        string[];   // QC Batch ID (= QCBatchID)
  id:             string[];   // Job ID
  test:           string[];
  matrix:         string[];   // Sample Matrix
  clientName:     string[];
  projectId:      string[];
  projectName:    string[];
  sampleLocation: string[];   // Site Name (SampleLogIn.StationLocationName)
  receivedFrom:   string;
  receivedTo:     string;
  analyzedFrom:   string;
  analyzedTo:     string;
  // Present-but-disabled ("SOON") until its column is wired:
  collectedFrom:  string;
  collectedTo:    string;
}

export const EMPTY_REE_FILTERS: ReeFilters = {
  batchId: [], id: [], test: [], matrix: [], clientName: [], projectId: [],
  projectName: [], sampleLocation: [],
  receivedFrom: '', receivedTo: '', analyzedFrom: '', analyzedTo: '',
  collectedFrom: '', collectedTo: '',
};

/** Distinct dropdown options for the panel. */
export interface ReeOptions {
  clients:         string[];
  projects:        string[];
  projectNames:    string[];
  tests:           string[];
  matrices:        string[];
  jobIds:          string[];
  sampleLocations: string[];
  batchIds:        ReeBatchOption[];   // QC Batch ID · Test · Method
  /** Per-value pending-sample counts (user request 2026-09-09, REE only), keyed by
   *  filter field. When a field's map is present its combo renders a 2-column
   *  "<Label> · Sample Count" picker; callers that omit `counts` (e.g. Result
   *  Tracking) keep single-column combos. */
  counts?: Partial<Record<'test' | 'matrix' | 'projectId' | 'projectName' | 'clientName' | 'sampleLocation', Record<string, number>>>;
  /** Min/max selectable date per date pair (yyyy-MM-dd), derived from the rows matching
   *  the current Test + other filters (user request 2026-09-09). When present, the date
   *  pickers are constrained to that range; callers that omit it keep unbounded pickers. */
  dateBounds?: Partial<Record<'analyzed' | 'received' | 'collected', { min?: string; max?: string }>>;
}

export const EMPTY_REE_OPTIONS: ReeOptions = {
  clients: [], projects: [], projectNames: [], tests: [], matrices: [],
  jobIds: [], sampleLocations: [], batchIds: [],
};

/** True when any filter the server honours has a value. */
export function hasActiveReeFilters(f: ReeFilters): boolean {
  return !!(f.batchId.length || f.id.length || f.test.length || f.matrix.length
    || f.clientName.length || f.projectId.length || f.projectName.length
    || f.sampleLocation.length || f.receivedFrom || f.receivedTo
    || f.analyzedFrom || f.analyzedTo || f.collectedFrom || f.collectedTo);
}

const toLookup = (col: string, values: string[]) => ({ cols: [col], rows: values.map(v => [v]) });

/** Popup width sized to the longest option text. */
const popW = (values: string[], header: string) => {
  const longest = values.reduce((m, v) => Math.max(m, v.length), header.length);
  return Math.min(380, Math.max(180, Math.round(longest * 7 + 78)));
};

interface Props {
  filters: ReeFilters;
  onChange: (patch: Partial<ReeFilters>) => void;
  options: ReeOptions;
  loadingOptions?: boolean;
  applying?: boolean;
  onApply: () => void;
  onClear: () => void;
  /** Controlled collapse state — when provided the parent owns open/close. */
  collapsed?: boolean;
  onCollapsedChange?: (c: boolean) => void;
  /** Pending-entry counts shown inside the panel's bottom bar. */
  pendingBatches?: number;
  pendingJobs?: number;
  pendingLoading?: boolean;
  /** Which date-pair columns render as "soon" (disabled, greyed) because the
   *  caller's data doesn't carry that granularity yet. Defaults to REE's own
   *  behaviour (only Date Collected is deferred) so existing callers are
   *  unaffected. */
  dateSoon?: { analyzed?: boolean; received?: boolean; collected?: boolean };
}

export function ResultEntryQueryPanel({
  filters, onChange, options,
  applying: _applying = false, onApply: _onApply, onClear: _onClear,
  collapsed: collapsedProp,
  pendingBatches, pendingJobs, pendingLoading = false,
  dateSoon,
}: Props) {
  const soonAnalyzed  = dateSoon?.analyzed  ?? false;
  const soonReceived  = dateSoon?.received  ?? false;
  const soonCollected = dateSoon?.collected ?? true;
  const [collapsedInternal] = useState(false);
  const collapsed = collapsedProp !== undefined ? collapsedProp : collapsedInternal;

  const recvError = useMemo(() =>
    filters.receivedFrom && filters.receivedTo && filters.receivedFrom > filters.receivedTo
      ? 'Date Received “From” must be on or before “To”.' : null,
    [filters.receivedFrom, filters.receivedTo]);
  const anaError = useMemo(() =>
    filters.analyzedFrom && filters.analyzedTo && filters.analyzedFrom > filters.analyzedTo
      ? 'Date Analyzed “From” must be on or before “To”.' : null,
    [filters.analyzedFrom, filters.analyzedTo]);
  const colError = useMemo(() =>
    filters.collectedFrom && filters.collectedTo && filters.collectedFrom > filters.collectedTo
      ? 'Date Collected “From” must be on or before “To”.' : null,
    [filters.collectedFrom, filters.collectedTo]);
  const dateError = recvError ?? anaError ?? colError;

  /** One single-column checked-combo field. */
  const combo = (label: string, values: string[], opts: string[], onToggle: (n: string[]) => void) => (
    <Field label={label}>
      <LookupCombo className="ree-lk" multi multiActions showFilter placeholder="All"
        popupClassName="ree-pick" popupWidth={popW(opts, label)}
        lookup={toLookup(label, opts)} values={values} onToggle={onToggle} />
    </Field>
  );

  /** A checked-combo that adds a "Sample Count" column when `counts` is supplied
   *  (user request 2026-09-09, REE); otherwise falls back to the single-column combo
   *  so callers without counts (Result Tracking) are unchanged. The option value is
   *  still the first column, so selection/cascade/Clear behave exactly as before. */
  const comboC = (label: string, values: string[], opts: string[],
                  counts: Record<string, number> | undefined, onToggle: (n: string[]) => void) => {
    if (!counts) return combo(label, values, opts, onToggle);
    const nameW = Math.min(300, Math.max(150, popW(opts, label) - 78 + 24));
    return (
      <Field label={label}>
        <LookupCombo className="ree-lk" multi multiActions showFilter placeholder="All"
          popupClassName="ree-pick" popupWidth={nameW + 100 + 28} colWidths={[nameW, 90]}
          lookup={{ cols: [label, 'Sample Count'], rows: opts.map(o => [o, String(counts[o] ?? 0)]) }}
          values={values} onToggle={onToggle} />
      </Field>
    );
  };

  /** One date input cell (From on row 1, or To on row 2 with an empty label).
   *  data-empty drives the CSS that hides the browser's native "mm/dd/yyyy"
   *  placeholder segments while the field has no value (user request
   *  2026-09-02) — it's state-driven (not a CSS-only guess), so a typed date
   *  is never hidden along with it. */
  const dateCell = (label: string, aria: string, value: string,
                    onVal: (v: string) => void, opts?: { soon?: boolean; error?: boolean; min?: string; max?: string }) => (
    <Field label={label} soon={opts?.soon} className={opts?.error ? 'ree-field-err' : undefined}>
      <input type="date" className="ree-date" aria-label={aria} disabled={opts?.soon}
        data-empty={value ? undefined : 'true'}
        min={opts?.min} max={opts?.max}
        title={opts?.soon ? 'Coming soon — not yet available in this build.' : undefined}
        value={value} onChange={(e) => onVal(e.target.value)} />
    </Field>
  );

  // When collapsed, the outer control bar's Query toggle is the only affordance —
  // the panel disappears entirely (no title band left behind).
  if (collapsed) return null;

  return (
    <section className="ree-qp" aria-label="Query Panel">
      {/* 7-column × 2-row grid — row-major fill: children 1-7 = row 1, 8-14 = row 2. */}
      <div className="ree-qp-grid">
        {/* ── Row 1 ── */}
        <Field label="QC Batch ID">
          {/* 3-column picker (user request 2026-09-09): QC Batch ID · Test · Method
              — the Method column was added and the batch's analytical Method now
              shows alongside Test (this supersedes the 2026-09-08 single-column REE
              layout). Test/Method correspond to the selected QC Batch record. */}
          <LookupCombo className="ree-lk" multi multiActions showFilter placeholder="All"
            autoFocus
            popupClassName="ree-pick" popupWidth={440} colWidths={[150, 150, 120]}
            lookup={{ cols: ['QC Batch ID', 'Test', 'Method'],
                      rows: options.batchIds.map(b => [b.id, b.test, b.method]) }}
            values={filters.batchId} onToggle={(n) => onChange({ batchId: n })} />
        </Field>
        {comboC('Sample Matrix', filters.matrix,     options.matrices, options.counts?.matrix,     (n) => onChange({ matrix: n }))}
        {comboC('Project ID',    filters.projectId,  options.projects, options.counts?.projectId,  (n) => onChange({ projectId: n }))}
        {comboC('Client',        filters.clientName, options.clients,  options.counts?.clientName, (n) => onChange({ clientName: n }))}
        {dateCell('Date Analyzed-From',  'Date Analyzed from',  filters.analyzedFrom,  (v) => onChange({ analyzedFrom: v }),  { error: !!anaError, soon: soonAnalyzed, min: options.dateBounds?.analyzed?.min, max: options.dateBounds?.analyzed?.max })}
        {dateCell('Date Received-From',  'Date Received from',  filters.receivedFrom,  (v) => onChange({ receivedFrom: v }),  { error: !!recvError, soon: soonReceived, min: options.dateBounds?.received?.min, max: options.dateBounds?.received?.max })}
        {dateCell('Date Collected-From', 'Date Collected from', filters.collectedFrom, (v) => onChange({ collectedFrom: v }), { error: !!colError, soon: soonCollected, min: options.dateBounds?.collected?.min, max: options.dateBounds?.collected?.max })}

        {/* ── Row 2 ── */}
        {combo('Job ID',          filters.id,             options.jobIds,          (n) => onChange({ id: n }))}
        {comboC('Test',         filters.test,          options.tests,           options.counts?.test,          (n) => onChange({ test: n }))}
        {comboC('Project Name', filters.projectName,   options.projectNames,    options.counts?.projectName,   (n) => onChange({ projectName: n }))}
        {comboC('Site Name',    filters.sampleLocation, options.sampleLocations, options.counts?.sampleLocation, (n) => onChange({ sampleLocation: n }))}
        {dateCell('Date Analyzed-To',  'Date Analyzed to',  filters.analyzedTo,  (v) => onChange({ analyzedTo: v }),  { error: !!anaError, soon: soonAnalyzed, min: options.dateBounds?.analyzed?.min, max: options.dateBounds?.analyzed?.max })}
        {dateCell('Date Received-To',  'Date Received to',  filters.receivedTo,  (v) => onChange({ receivedTo: v }),  { error: !!recvError, soon: soonReceived, min: options.dateBounds?.received?.min, max: options.dateBounds?.received?.max })}
        {dateCell('Date Collected-To', 'Date Collected to', filters.collectedTo, (v) => onChange({ collectedTo: v }), { error: !!colError, soon: soonCollected, min: options.dateBounds?.collected?.min, max: options.dateBounds?.collected?.max })}
      </div>
      {dateError && <div className="ree-inline-err ree-inline-err-row" role="alert">{dateError}</div>}

      {/* Pending-entry summary — shown at the bottom of the filters panel */}
      {(pendingBatches !== undefined || pendingJobs !== undefined) && (
        <div className="ree-qp-actions">
          <div className="ree-qp-pending">
            <i className="ti ti-clock-hour-4" aria-hidden="true" />
            <span className="ree-qp-pending-label">Pending Result Entry</span>
            <span className="ree-qp-pending-stat">
              <span className="ree-qp-pending-name">QC Batch</span>
              <span className={`ree-qp-pending-count${pendingLoading ? ' ree-qp-pending-loading' : (pendingBatches ?? 0) === 0 ? ' ree-qp-pending-zero' : ''}`}>
                {pendingLoading ? '…' : (pendingBatches ?? 0)}
              </span>
            </span>
            <span className="ree-qp-pending-stat">
              <span className="ree-qp-pending-name">Job ID</span>
              <span className={`ree-qp-pending-count${pendingLoading ? ' ree-qp-pending-loading' : (pendingJobs ?? 0) === 0 ? ' ree-qp-pending-zero' : ''}`}>
                {pendingLoading ? '…' : (pendingJobs ?? 0)}
              </span>
            </span>
          </div>
        </div>
      )}
    </section>
  );
}

/** One compact label/field cell. An empty `label` renders a spacer so the row-2
 *  date "To" inputs align with the labelled controls beside them. */
function Field({ label, soon, className, children }: {
  label: string; soon?: boolean; className?: string; children: React.ReactNode;
}) {
  return (
    <div className={`ree-field${className ? ` ${className}` : ''}`}>
      <label className="ree-lbl">
        <span title={label || undefined}>{label || ' '}</span>
        {soon && <span className="ree-soon" title="Coming soon — not yet available in this build.">soon</span>}
      </label>
      <div className="ree-ctrl">{children}</div>
    </div>
  );
}
